import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { fetchDeities } from "@/lib/api";
import { MAHAVIDYAS, mvImageFor } from "@/lib/mahavidyas";
import { GANAPATI_IMG } from "@/pages/Ganapati32";
import JourneyStrip from "@/components/JourneyStrip";
import { resolveImage } from "@/lib/images";
import { stripHtml } from "@/lib/html";
import { MandalaMotif } from "@/components/MotifBackground";
import { ArrowUpRight, Sparkles } from "lucide-react";

const onImgError = (e) => { e.currentTarget.style.opacity = "0"; };

export default function Deities() {
  const { pick, isDeva } = useLang();
  const settings = useSettings();
  const { deity_categories = [] } = settings;
  const [deities, setDeities] = useState([]);
  const [cat, setCat] = useState("all");
  useEffect(() => { fetchDeities().then(setDeities).catch(() => {}); }, []);

  const cats = (deity_categories || []).filter((c) => c && c.key);
  const filtered = cat === "all" ? deities : deities.filter((d) => (d.category || "") === cat);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="deities-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--parchment)]">
        <MandalaMotif className="pointer-events-none absolute -right-24 -top-16 hidden h-[460px] w-[460px] text-[var(--gold)] opacity-[0.07] md:block" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <p className="overline-label text-gold">{pick("देवी-देवता", "Deities")}</p>
          <h1 className={`mt-3 text-4xl leading-tight text-ink deva-lh md:text-6xl ${isDeva ? "font-deva" : "font-serif-latin"}`} data-testid="page-hero-title">{pick("देवी-देवता", "Deities")}</h1>
          <p className={`mt-3 text-xl text-ink-2 md:text-2xl ${isDeva ? "font-serif-latin" : "font-deva deva-lh"}`}>{pick("The Deities of Sanatana Dharma", "देवी-देवता")}</p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-2 md:text-lg">
            {pick(
              "प्रत्येक देव — उनके मंत्र, सूक्त, स्तोत्र, पूजा विधि एवं मंदिर, एक ही स्थान पर।",
              "Each deity — their mantras, suktas, stotras, puja vidhis and temples, gathered in one place."
            )}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
        {cats.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="deity-category-filter">
            <button onClick={() => setCat("all")} data-testid="deity-cat-all" className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === "all" ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>{pick("सभी", "All")}</button>
            {cats.map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`deity-cat-${c.key}`} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === c.key ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>{c.label}</button>
            ))}
          </div>
        )}
        {/* Featured deity banner */}
        {featured && (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <Link to={`/deities/${featured.slug}`} data-testid={`deity-featured-${featured.slug}`}
              className="group grid overflow-hidden border border-line bg-surface transition-colors hover:border-[var(--gold)] md:grid-cols-2">
              <div className="relative min-h-[280px] overflow-hidden md:min-h-[400px]">
                <img src={resolveImage({ deity: featured.name })} alt={featured.name} onError={onImgError}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute left-6 top-6 inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-black/30 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                  <Sparkles className="h-3.5 w-3.5" /> {pick("प्रमुख देव", "Principal Deity")}
                </span>
                <p className="absolute bottom-6 left-6 font-deva text-4xl text-white deva-lh md:text-5xl">{featured.deva_name}</p>
              </div>
              <div className="flex flex-col justify-center p-7 md:p-10">
                <h2 className="font-serif-latin text-3xl text-ink md:text-4xl">{featured.name}</h2>
                {featured.epithet && <p className="mt-2 text-base text-gold">{featured.epithet}</p>}
                {featured.domain && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {String(featured.domain).split(",").slice(0, 4).map((d) => (
                      <span key={d} className="border border-line px-2.5 py-1 text-xs text-ink-2">{d.trim()}</span>
                    ))}
                  </div>
                )}
                <p className="mt-5 text-[15px] leading-relaxed text-ink-2 line-clamp-4">{stripHtml(featured.significance || featured.description)}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-saffron">
                  {pick("दर्शन करें", "Explore")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((d, i) => (
            <motion.div key={d.slug}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
              <Link to={`/deities/${d.slug}`} data-testid={`deity-card-${d.slug}`}
                className="group flex h-full flex-col overflow-hidden border border-line bg-surface transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-lg">
                <div className="relative h-56 overflow-hidden bg-[var(--surface-alt)]">
                  <img src={resolveImage({ deity: d.name })} alt={d.name} loading="lazy" onError={onImgError}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/5 to-transparent" />
                  <p className="absolute bottom-4 left-5 font-deva text-3xl text-white deva-lh">{d.deva_name}</p>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif-latin text-xl text-ink">{d.name}</h3>
                  {d.epithet && <p className="mt-1 text-sm text-gold">{d.epithet}</p>}
                  {d.domain && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {String(d.domain).split(",").slice(0, 3).map((x) => (
                        <span key={x} className="border border-line px-2 py-0.5 text-[11px] text-ink-3">{x.trim()}</span>
                      ))}
                    </div>
                  )}
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{stripHtml(d.description)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-saffron opacity-0 transition-opacity group-hover:opacity-100">
                    {pick("दर्शन", "Explore")} <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Collections — Dasha Mahavidya & 32 Forms of Ganapati (folded into Deities) */}
        <div className="mt-16">
          <p className="overline-label text-gold">{pick("विशेष संग्रह", "Special collections")}</p>
          <h2 className="mt-1 font-serif-latin text-2xl text-ink md:text-3xl">{pick("और गहराई से जानें", "Explore more deeply")}</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <Link to="/mahavidya" data-testid="deities-mahavidya-band"
              className="group flex flex-col gap-5 overflow-hidden border border-line bg-[var(--surface)] p-6 transition-all duration-300 hover:border-[var(--gold)] hover:shadow-lg sm:flex-row sm:items-center">
              <div className="flex -space-x-4">
                {MAHAVIDYAS.slice(0, 4).map((m) => (
                  <img key={m.slug} src={mvImageFor(m, settings)} alt={m.en} loading="lazy"
                    className="h-20 w-16 shrink-0 rounded-md border-2 border-[var(--surface)] object-cover shadow-md" />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-deva text-xl text-ink deva-lh">{pick("दस महाविद्या", "The Dasha Mahavidya")}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{pick("ज्ञान की दस महाशक्तियाँ — काली, तारा, त्रिपुरसुन्दरी एवं अन्य।", "The ten great wisdom goddesses — Kali, Tara, Tripura Sundari and the rest.")}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-saffron transition-transform group-hover:translate-x-0.5">{pick("दर्शन करें", "Explore")} <ArrowUpRight className="h-4 w-4" /></span>
              </div>
            </Link>

            <Link to="/ganapati-32" data-testid="deities-ganapati32-band"
              className="group flex flex-col gap-5 overflow-hidden border border-line bg-[var(--surface)] p-6 transition-all duration-300 hover:border-[var(--gold)] hover:shadow-lg sm:flex-row sm:items-center">
              <img src={GANAPATI_IMG} alt="32 Forms of Ganapati" loading="lazy"
                className="h-28 w-28 shrink-0 rounded-md object-cover shadow-md" />
              <div className="min-w-0 flex-1">
                <p className="font-deva text-xl text-ink deva-lh">{pick("बत्तीस गणपति", "32 Forms of Ganapati")}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{pick("श्रीतत्त्वनिधि अनुसार विनायक के बत्तीस पूज्य स्वरूप।", "The thirty-two revered forms of Vinayaka, per the Sritattvanidhi.")}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-saffron transition-transform group-hover:translate-x-0.5">{pick("दर्शन करें", "Explore")} <ArrowUpRight className="h-4 w-4" /></span>
              </div>
            </Link>
          </div>
        </div>

        <JourneyStrip links={["temples", "pilgrimage", "calendar", "festivals", "panchang"]} />
      </section>
    </div>
  );
}
